import React, { useEffect, useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    TextField,
    Box,
    Typography,
    Grid,
    MenuItem,
    Avatar,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment/moment";
import Select from "react-select";
import { stageOptions, blockOptions_1, blockOptions_2, componentOptions, gfcStatusOptions } from "helpers/designEngineeringOptions";
import { useCreateDesignEngMutation } from "store/slices/apis/designEngineeringSlice";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { useParams } from "react-router-dom";
import { useLazyGetAllDesignEngQuery } from "store/slices/apis/designEngineeringSlice";
import DrawingTitleSearch from "./DrawingTitleSearch";
import CircularProgress from "@mui/material/CircularProgress";
import WbsTreeView from "view/dashboard/wbsTreeView";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const calenderSVG = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_894_610)">
            <path d="M12.6667 1.33333H12V0.666667C12 0.489856 11.9298 0.320286 11.8047 0.195262C11.6797 0.0702379 11.5101 0 11.3333 0C11.1565 0 10.987 0.0702379 10.8619 0.195262C10.7369 0.320286 10.6667 0.489856 10.6667 0.666667V1.33333H5.33333V0.666667C5.33333 0.489856 5.2631 0.320286 5.13807 0.195262C5.01305 0.0702379 4.84348 0 4.66667 0C4.48986 0 4.32029 0.0702379 4.19526 0.195262C4.07024 0.320286 4 0.489856 4 0.666667V1.33333H3.33333C2.4496 1.33439 1.60237 1.68592 0.97748 2.31081C0.352588 2.93571 0.00105857 3.78294 0 4.66667L0 12.6667C0.00105857 13.5504 0.352588 14.3976 0.97748 15.0225C1.60237 15.6474 2.4496 15.9989 3.33333 16H12.6667C13.5504 15.9989 14.3976 15.6474 15.0225 15.0225C15.6474 14.3976 15.9989 13.5504 16 12.6667V4.66667C15.9989 3.78294 15.6474 2.93571 15.0225 2.31081C14.3976 1.68592 13.5504 1.33439 12.6667 1.33333ZM1.33333 4.66667C1.33333 4.13623 1.54405 3.62753 1.91912 3.25245C2.29419 2.87738 2.8029 2.66667 3.33333 2.66667H12.6667C13.1971 2.66667 13.7058 2.87738 14.0809 3.25245C14.456 3.62753 14.6667 4.13623 14.6667 4.66667V5.33333H1.33333V4.66667ZM12.6667 14.6667H3.33333C2.8029 14.6667 2.29419 14.456 1.91912 14.0809C1.54405 13.7058 1.33333 13.1971 1.33333 12.6667V6.66667H14.6667V12.6667C14.6667 13.1971 14.456 13.7058 14.0809 14.0809C13.7058 14.456 13.1971 14.6667 12.6667 14.6667Z" fill="#433F5E" />
            <path d="M8 11C8.55228 11 9 10.5523 9 10C9 9.44772 8.55228 9 8 9C7.44772 9 7 9.44772 7 10C7 10.5523 7.44772 11 8 11Z" fill="#433F5E" />
            <path d="M4.66656 11C5.21885 11 5.66656 10.5523 5.66656 10C5.66656 9.44772 5.21885 9 4.66656 9C4.11428 9 3.66656 9.44772 3.66656 10C3.66656 10.5523 4.11428 11 4.66656 11Z" fill="#433F5E" />
            <path d="M11.3334 11C11.8857 11 12.3334 10.5523 12.3334 10C12.3334 9.44772 11.8857 9 11.3334 9C10.7811 9 10.3334 9.44772 10.3334 10C10.3334 10.5523 10.7811 11 11.3334 11Z" fill="#433F5E" />
        </g>
        <defs>
            <clipPath id="clip0_894_610">
                <rect width="16" height="16" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const AddDesignEngineer = (props) => {
    const { open, handleClose, refetchCounts } = props;
    const [revisionCount, setRevisionCount] = useState(1);
    const [createDesignEng] = useCreateDesignEngMutation()
    const dispatch = useDispatch();
    const { projectId } = useParams();

    const [triggerSearch, { data: searchData, isFetching }] = useLazyGetAllDesignEngQuery();

    const module = "DesignAndEngineeringTracker";

    const { data: documentTypeOptionsRes } = useGetMasterDataQuery({ module: module, category: 'DocumentType', ProjectId: projectId });
    const documentTypeOptionsData = documentTypeOptionsRes?.data || [];
    const documentTypeOptions = mapAndSortAlphabetically(documentTypeOptionsData);
    // const documentTypeOptions = (documentTypeOptionsData || []).map(p => ({
    //     value: p?.value,
    //     label: p?.value,
    // }));

    const { data: consultantOptionsRes } = useGetMasterDataQuery({ module: module, category: 'Consultant', ProjectId: projectId });
    const consultantOptionsData = consultantOptionsRes?.data || [];
    const consultantOptions = (consultantOptionsData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const { data: disciplineOptionsRes } = useGetMasterDataQuery({ module: module, category: 'Discipline', ProjectId: projectId });
    const disciplineOptionsData = disciplineOptionsRes?.data || [];
    const disciplineOptions = mapAndSortAlphabetically(disciplineOptionsData);
    // const disciplineOptions = (disciplineOptionsData || []).map(p => ({
    //     value: p?.value,
    //     label: p?.value,
    // }));

    const { data: statusOptionsRes } = useGetMasterDataQuery({ module: module, category: 'Status', ProjectId: projectId });
    const statusOptionsData = statusOptionsRes?.data || [];
    const statusOptions = (statusOptionsData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const blockOptionsByComponent = {
        Component1: blockOptions_1,
        Component2: blockOptions_2,
    };

    const initialFormData = {
        consultant: "",
        discipline: "",
        stage: "",
        designType: "",
        wbsNamePath: "",
        //block: "",
        drgNo: "",
        title: "",
        status: "",
        targetDate: null,
        remarks: "",
        gfcApprovalStatus: "",
        gfcApprovalDate: null,
        //component: "",
        submissions: [
            {
                submissionDate: null,
                revisionNo: ""
            }
        ]
    };

    const getValidationSchema = (count) => {
        let schema = {
            consultant: Yup.string().required("Consultant field is required"),
            discipline: Yup.string().required("Discipline field is required"),
            //stage: Yup.string().required("Stage field is required"),
            title: Yup.string().required("Title field is required"),
            status: Yup.string().required("Status field is required"),
            // targetDate: Yup.date()
            //     .required("Target Date field is required")
            //     .typeError("Please enter a valid date"),
            // gfcApprovalStatus: Yup.string().required(" GFC Status field is required"),
            // gfcApprovalDate: Yup.date()
            //     .required("GFC Date field is required")
            //     .typeError("Please enter a valid date"),
            designType: Yup.string().required("Document Type is required"),
            wbsNamePath: Yup.string().required("Asset is required"),
            //block: Yup.string().required("Block field is required"),
            drgNo: Yup.string().required("Drawing Number field is required"),
            // remarks: Yup.string().required("Remarks field is required"),
            //component: Yup.string().required("Component field is required"),
            // submissions: Yup.array()
            //     .of(
            //         Yup.object({
            //             submissionDate: Yup.date()
            //                 .nullable()
            //                 .typeError("Please enter a valid date"),
            //             revisionNo: Yup.string()
            //                 .nullable()
            //         })
            //     )
        };

        return Yup.object(schema);
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const handleAddMoreClick = (values, setFieldValue) => {
        const newSubmission = {
            submissionDate: "",
            revisionNo: ""
        };

        setFieldValue('submissions', [...values.submissions, newSubmission]);
        setRevisionCount(prev => prev + 1);
    };

    const mapWbsIdsToSubcats = (wbsIdsPath) => {
        const ids = wbsIdsPath
            ? wbsIdsPath.split(",").map(id => id.trim())
            : [];

        return {
            subDesign1: ids[0] ?? "",
            subDesign2: ids[1] ?? "",
            subDesign3: ids[2] ?? "",
            subDesign4: ids[3] ?? "",
            subDesign5: ids[4] ?? "",
        };
    }

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        const filteredSubmissions = values.submissions.filter(submission =>
            submission.submissionDate || submission.revisionNo
        );


        const formatDate = (dateString) => {
            if (!dateString || dateString === "" || dateString === "Invalid Date") {
                return null;
            }
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date.toISOString();
        };

        const subcatPayload = mapWbsIdsToSubcats(values.wbsIdsPath);

        const formattedData = {
            //projectCode: "string",
            projectId: projectId,
            consultant: values.consultant,
            discipline: values.discipline,
            stage: values.stage,
            //block: values.block,
            designType: values.designType,
            projectCode: values.wbsNamePath,
            ...subcatPayload,
            drgNo: values.drgNo,
            title: values.title,
            //component: values.component,
            status: values.status,
            targetDate: formatDate(values.targetDate) || null,
            gfcApprovalStatus: values.gfcApprovalStatus,
            gfcApprovalDate: formatDate(values.gfcApprovalDate) || null,
            remarks: values.remarks,
            submissions: filteredSubmissions.map(submission => ({
                submissionDate: formatDate(submission.submissionDate),
                revisionNo: submission.revisionNo || ""
            }))
        };



        try {
            const response = await createDesignEng({ body: formattedData }).unwrap();
            refetchCounts()
            openNotification("Drawing added successfully!", "success");
            resetForm();
            setRevisionCount(1);
            setSubmitting(false);
            handleClose();
        } catch (error) {
            openNotification(error?.data?.message ?? "Something went wrong", "error");
            setSubmitting(false);
        }
    };

    const renderRevisionFields = (values, handleChange, handleBlur, touched, errors, setFieldValue, setFieldTouched) => {
        const fields = [];

        values.submissions.forEach((submission, index) => {
            fields.push(
                <React.Fragment key={`submission-${index}`}>

                    {index > 0 && (
                        <Grid size={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
                                <Button
                                    onClick={() => handleDeleteRevision(index, values, setFieldValue)}
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '12px',
                                        padding: '2px 10px',
                                        minWidth: 'auto',
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                >
                                    Delete Revision {index > 0 ? `(${index + 1})` : ''}
                                </Button>
                            </Box>
                        </Grid>
                    )}

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                            Submitted Date {index > 0 ? `(${index + 1})` : ''}
                        </Typography>
                        <TextField
                            type="date"
                            name={`submissions[${index}].submissionDate`}
                            value={values.submissions[index].submissionDate}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onClick={(e) => {
                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                if (input) input.showPicker?.();
                            }}
                            fullWidth
                            sx={{
                                ...rightTextStyle,
                                "& input::-webkit-calendar-picker-indicator": {
                                    display: "none",
                                    WebkitAppearance: "none",
                                },
                                "& input[type='date']": {
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        border: "none",
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <Box sx={{
                                        ml: 2,
                                        mr: 1,
                                        color: "#433F5E",
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer"
                                    }}>
                                        {calenderSVG}
                                    </Box>
                                ),
                                sx: {
                                    backgroundColor: "#fff",
                                    border: '1px solid',
                                    borderColor: 'hsl(0, 0%, 80%)',
                                    height: "40px",
                                    width: { xs: "100%", md: "100%" },
                                    borderRadius: "5px",
                                },
                            }}
                        />
                        {touched.submissions &&
                            touched.submissions[index] &&
                            touched.submissions[index].submissionDate &&
                            errors.submissions &&
                            errors.submissions[index] &&
                            errors.submissions[index].submissionDate && (
                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                    {errors.submissions[index].submissionDate}
                                </Typography>
                            )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography sx={labelTextStyle}>
                            Revision Number {index > 0 ? `(${index + 1})` : ''}
                        </Typography>
                        <TextField
                            fullWidth
                            name={`submissions[${index}].revisionNo`}
                            value={values.submissions[index].revisionNo}
                            onChange={(e) => {
                                setFieldValue(`submissions[${index}].revisionNo`, e.target.value);
                            }}
                            onBlur={handleBlur}
                            placeholder="Enter Revision Number (Optional)"
                            InputProps={{
                                sx: {
                                    height: "40px",
                                    borderRadius: "8px",
                                    backgroundColor: "#fff",
                                    "& input::placeholder": {
                                        fontWeight: 400,
                                        fontSize: "16px",
                                        lineHeight: "100%",
                                        letterSpacing: "0.16px",
                                        color: "#0A0A0C",
                                        fontFamily: "Outfit, sans-serif",
                                        opacity: 1,
                                    },
                                    "& input": {
                                        fontWeight: 400,
                                        fontSize: "16px",
                                        lineHeight: "100%",
                                        letterSpacing: "0.16px",
                                        color: "#0A0A0C",
                                        fontFamily: "Outfit, sans-serif",
                                        "&:-webkit-autofill": {
                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                            WebkitTextFillColor: "#0A0A0C",
                                        },
                                        "&:-webkit-autofill:hover": {
                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                            WebkitTextFillColor: "#0A0A0C",
                                        },
                                        "&:-webkit-autofill:focus": {
                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                            WebkitTextFillColor: "#0A0A0C",
                                        },
                                    },
                                },

                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Box sx={{
                            p: 0, width: "100%", height: "1px", borderRadius: "4px", bgcolor: "#e0e0e0"
                        }}></Box>
                    </Grid>
                </React.Fragment>
            );
        });

        return fields;
    };


    const handleDeleteRevision = (indexToDelete, values, setFieldValue) => {

        const updatedSubmissions = values.submissions.filter((_, index) => index !== indexToDelete);


        const recalculatedSubmissions = updatedSubmissions.map((submission, index) => ({
            ...submission,
            revisionNo: `R${index}`
        }));


        setFieldValue('submissions', recalculatedSubmissions);
        setRevisionCount(prev => prev - 1);
    };


    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    backgroundColor: "#FFFFFF",
                    width: "100%",
                    overflowX: "hidden"
                },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
            }}>
                <Box>
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: "27px",
                        lineHeight: "100%",
                        color: "#140F36",
                        fontFamily: "Outfit, sans-serif"
                    }}>
                        Add Drawing
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Formik
                    initialValues={initialFormData}
                    validationSchema={getValidationSchema(revisionCount)}
                    onSubmit={handleSubmit}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                        setFieldTouched,
                        setFieldError,
                    }) => {

                        const blockOptionsByComponent = {
                            // 🔁 put your real component values here
                            // Example:
                            // "COMP_1": blockOptions_1,
                            // "COMP_2": blockOptions_2,
                            [componentOptions?.[0]?.value]: blockOptions_1,
                            [componentOptions?.[1]?.value]: blockOptions_2,
                        };

                        const selectedBlockOptions =
                            blockOptionsByComponent[values.component] ?? [];

                        const hasSearchResults = Array.isArray(searchData?.data) && searchData.data.length > 0;

                        return (
                            <>
                                <DrawingTitleSearch
                                    component={values.component}
                                    block={values.block}
                                    consultant={values.consultant}
                                    discipline={values.discipline}
                                    projectId={projectId}
                                    onSearch={triggerSearch}
                                />

                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Grid container spacing={2}>
                                                <Grid size={12}>
                                                    <Typography sx={{
                                                        mt: 1,
                                                        mb: 3,
                                                        fontSize: "22px",
                                                        fontWeight: 500,
                                                        fontFamily: "Outfit, sans-serif",
                                                        color: "#0A0A0C"
                                                    }}>
                                                        Asset Selector
                                                    </Typography>

                                                    <WbsTreeView
                                                        projectId={projectId}
                                                        parentId="683"
                                                        onSelect={function (node) {
                                                            // node = { id, name, raw, isLeaf }

                                                            // Save into form values
                                                            setFieldValue("wbsId", node.id, false);
                                                            setFieldValue("wbsNamePath", node.fullPath, false);
                                                            setFieldValue("wbsIdsPath", node.idPath, false);

                                                            setFieldTouched("wbsNamePath", true, false);

                                                            setTimeout(() => {
                                                                setFieldError("wbsNamePath", undefined);
                                                            }, 0);
                                                        }}
                                                    />

                                                    {/* Optional: show selected WBS name inside popup */}
                                                    {values.wbsNamePath && (
                                                        <Typography sx={{ mt: 2, fontSize: 14, color: "#555" }}>
                                                            Selected Asset: <strong>{values.wbsNamePath}</strong>
                                                        </Typography>
                                                    )}

                                                    {touched.wbsNamePath && errors.wbsNamePath && !values.wbsNamePath && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.wbsNamePath}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Grid container spacing={2}>
                                                <Grid size={12}>
                                                    <Typography sx={{
                                                        mt: 1,
                                                        mb: 0,
                                                        fontSize: "22px",
                                                        fontWeight: 500,
                                                        fontFamily: "Outfit, sans-serif",
                                                        color: "#0A0A0C"
                                                    }}>
                                                        Drawing Details
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                        Select Document Type
                                                    </Typography>
                                                    <Select
                                                        name="designType"
                                                        options={documentTypeOptions}
                                                        value={documentTypeOptions.find(opt => opt.value === values.designType) || null}
                                                        onChange={(option) => {
                                                            const comp = option?.value ?? "";
                                                            setFieldValue("designType", comp);
                                                        }}
                                                        onBlur={() => setFieldTouched("designType", true)}
                                                        placeholder="Select Document Type"
                                                    />
                                                    {touched.designType && errors.designType && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.designType}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                {/* <Grid size={12}>
                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                        Select Component
                                                    </Typography>
                                                    <Select
                                                        name="component"
                                                        options={componentOptions}
                                                        value={componentOptions.find(opt => opt.value === values.component) || null}
                                                        onChange={(option) => {
                                                            const comp = option?.value ?? "";
                                                            setFieldValue("component", comp);

                                                            // reset dependent field
                                                            setFieldValue("block", "");
                                                            setFieldTouched("block", false, false);
                                                        }}
                                                        onBlur={() => setFieldTouched("component", true)}
                                                        placeholder="Select Component"
                                                    />
                                                    {touched.component && errors.component && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.component}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid size={12}>
                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                        Select Block
                                                    </Typography>
                                                    <Select
                                                        name="block"
                                                        options={selectedBlockOptions}
                                                        value={selectedBlockOptions.find(opt => opt.value === values.block) || null}
                                                        //value={blockOptions.find(opt => opt.value === values.block) || null}
                                                        onChange={(option) => setFieldValue("block", option.value)}
                                                        onBlur={() => setFieldTouched("block", true)}
                                                        placeholder="Select Block"
                                                    />
                                                    {touched.block && errors.block && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.block}
                                                        </Typography>
                                                    )}
                                                </Grid> */}
                                                <Grid size={12}>
                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                        Select Consultant
                                                    </Typography>
                                                    <Select
                                                        name="consultant"
                                                        options={consultantOptions}
                                                        value={consultantOptions.find(opt => opt.value === values.consultant) || null}
                                                        onChange={(option) => setFieldValue("consultant", option.value)}
                                                        onBlur={() => setFieldTouched("consultant", true)}
                                                        placeholder="Select Consultant"
                                                    />
                                                    {touched.consultant && errors.consultant && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.consultant}
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                <Grid size={12}>
                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                        Select Discipline
                                                    </Typography>
                                                    <Select
                                                        name="discipline"
                                                        options={disciplineOptions}
                                                        value={disciplineOptions.find(opt => opt.value === values.discipline) || null}
                                                        onChange={(option) => setFieldValue("discipline", option.value)}
                                                        onBlur={() => setFieldTouched("discipline", true)}
                                                        placeholder="Select Discipline"
                                                    />
                                                    {touched.discipline && errors.discipline && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.discipline}
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                {values.component &&
                                                    values.block &&
                                                    values.consultant &&
                                                    values.discipline && hasSearchResults && (
                                                        <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 2, mb: 1, p: 2, border: "2px solid #ECECEC", borderRadius: 2 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: "14px",
                                                                    mb: 2,
                                                                    color: "#5F5F5F",
                                                                }}
                                                            >
                                                                We found the below drawings already part of DCI. Please ensure that you do not add duplicate drawing to DCI.
                                                            </Typography>
                                                            {isFetching ? (
                                                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                                                    <CircularProgress size={24} />
                                                                </Box>
                                                            ) : (
                                                                <TableContainer component={Paper}
                                                                    sx={{
                                                                        maxHeight: searchData?.data?.length > 3 ? 180 : "auto",
                                                                        overflowY: searchData?.data?.length > 3 ? "auto" : "visible",
                                                                        //boxShadow: "none",
                                                                        //borderBottom: "none",
                                                                    }}>
                                                                    <Table size="medium">
                                                                        <TableHead>
                                                                            <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
                                                                                <TableCell sx={{ fontWeight: 500 }}>
                                                                                    Drawing Number
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontWeight: 500 }}>
                                                                                    Drawing Title
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </TableHead>

                                                                        <TableBody>

                                                                            {searchData?.data.map((row, index) => (
                                                                                <TableRow key={index}>
                                                                                    <TableCell sx={{ fontSize: "13px" }}>{row.drgNo}</TableCell>
                                                                                    <TableCell sx={{ fontSize: "13px" }}>{row.title}</TableCell>
                                                                                </TableRow>
                                                                            ))
                                                                            }
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            )}
                                                        </Grid>
                                                    )}

                                                {/* <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Select Stage
                                    </Typography>
                                    <Select
                                        name="stage"
                                        options={stageOptions}
                                        value={stageOptions.find(opt => opt.value === values.stage) || null}
                                        onChange={(option) => setFieldValue("stage", option.value)}
                                        onBlur={() => setFieldTouched("stage", true)}
                                        placeholder="Select Stage"
                                    />
                                    {touched.stage && errors.stage && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.stage}
                                        </Typography>
                                    )}
                                </Grid> */}



                                                <Grid size={12}>
                                                    <Typography sx={labelTextStyle}>
                                                        Drawing Title
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="title"
                                                        value={values.title}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter Drawing Title"
                                                        InputProps={{
                                                            sx: {
                                                                height: "40px",
                                                                borderRadius: "8px",
                                                                backgroundColor: "#fff",
                                                                "& input::placeholder": {
                                                                    fontWeight: 400,
                                                                    fontSize: "16px",
                                                                    lineHeight: "100%",
                                                                    letterSpacing: "0.16px",
                                                                    color: "#0A0A0C",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                    opacity: 1,
                                                                },
                                                                "& input": {
                                                                    fontWeight: 400,
                                                                    fontSize: "16px",
                                                                    lineHeight: "100%",
                                                                    letterSpacing: "0.16px",
                                                                    color: "#0A0A0C",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                    "&:-webkit-autofill": {
                                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                        WebkitTextFillColor: "#0A0A0C",
                                                                    },
                                                                    "&:-webkit-autofill:hover": {
                                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                        WebkitTextFillColor: "#0A0A0C",
                                                                    },
                                                                    "&:-webkit-autofill:focus": {
                                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                        WebkitTextFillColor: "#0A0A0C",
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                    {touched.title && errors.title && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.title}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid size={12}>
                                                    <Typography sx={labelTextStyle}>
                                                        Drawing Number
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="drgNo"
                                                        value={values.drgNo}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Enter Drawing Number"
                                                        InputProps={{
                                                            sx: {
                                                                height: "40px",
                                                                borderRadius: "8px",
                                                                backgroundColor: "#fff",
                                                                "& input::placeholder": {
                                                                    fontWeight: 400,
                                                                    fontSize: "16px",
                                                                    lineHeight: "100%",
                                                                    letterSpacing: "0.16px",
                                                                    color: "#0A0A0C",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                    opacity: 1,
                                                                },
                                                                "& input": {
                                                                    fontWeight: 400,
                                                                    fontSize: "16px",
                                                                    lineHeight: "100%",
                                                                    letterSpacing: "0.16px",
                                                                    color: "#0A0A0C",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                    "&:-webkit-autofill": {
                                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                        WebkitTextFillColor: "#0A0A0C",
                                                                    },
                                                                    "&:-webkit-autofill:hover": {
                                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                        WebkitTextFillColor: "#0A0A0C",
                                                                    },
                                                                    "&:-webkit-autofill:focus": {
                                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                        WebkitTextFillColor: "#0A0A0C",
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                    {touched.drgNo && errors.drgNo && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.drgNo}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid size={12}>
                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                        Select Status
                                                    </Typography>
                                                    <Select
                                                        name="status"
                                                        options={statusOptions}
                                                        value={statusOptions.find(opt => opt.value === values.status) || null}
                                                        onChange={(option) => setFieldValue("status", option.value)}
                                                        onBlur={() => setFieldTouched("status", true)}
                                                        placeholder="Select Status"
                                                    />
                                                    {touched.status && errors.status && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.status}
                                                        </Typography>
                                                    )}
                                                </Grid>


                                                <Grid size={12}>
                                                    <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                        Target Date
                                                    </Typography>
                                                    <TextField
                                                        type="date"
                                                        name="targetDate"
                                                        value={values.targetDate}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        onClick={(e) => {
                                                            const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                            if (input) input.showPicker?.();
                                                        }}
                                                        fullWidth
                                                        sx={{
                                                            ...rightTextStyle,
                                                            "& input::-webkit-calendar-picker-indicator": {
                                                                display: "none",
                                                                WebkitAppearance: "none",
                                                            },
                                                            "& input[type='date']": {
                                                                appearance: "none",
                                                                WebkitAppearance: "none",
                                                            },
                                                            "& .MuiOutlinedInput-root": {
                                                                "& fieldset": {
                                                                    border: "none",
                                                                },
                                                            },
                                                        }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <Box sx={{
                                                                    ml: 2,
                                                                    mr: 1,
                                                                    color: "#433F5E",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    cursor: "pointer"
                                                                }}>
                                                                    {calenderSVG}
                                                                </Box>
                                                            ),
                                                            sx: {
                                                                backgroundColor: "#fff",
                                                                border: '1px solid',
                                                                borderColor: 'hsl(0, 0%, 80%)',
                                                                height: "40px",
                                                                width: { xs: "100%", md: "100%" },
                                                                borderRadius: "5px",
                                                            },
                                                        }}
                                                    />
                                                    {touched.targetDate && errors.targetDate && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.targetDate}
                                                        </Typography>
                                                    )}
                                                </Grid>


                                                {/* <Grid size={12}>
                                    <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "#140F36", fontFamily: "Outfit, sans-serif", mb: 1, mt: 1 }}>
                                        Approval Section
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Select GFC Approval Status
                                    </Typography>
                                    <Select
                                        name="gfcApprovalStatus"
                                        options={gfcStatusOptions}
                                        value={gfcStatusOptions.find(opt => opt.value === values.gfcApprovalStatus) || null}
                                        onChange={(option) => setFieldValue("gfcApprovalStatus", option.value)}
                                        onBlur={() => setFieldTouched("gfcApprovalStatus", true)}
                                        placeholder="Select GFC Approval Status"
                                    />
                                    {touched.gfcApprovalStatus && errors.gfcApprovalStatus && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.gfcApprovalStatus}
                                        </Typography>
                                    )}
                                </Grid>


                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                        GFC Approval Date
                                    </Typography>
                                    <TextField
                                        type="date"
                                        name="gfcApprovalDate"
                                        value={values.gfcApprovalDate}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        onClick={(e) => {
                                            const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                            if (input) input.showPicker?.();
                                        }}
                                        fullWidth
                                        sx={{
                                            ...rightTextStyle,
                                            "& input::-webkit-calendar-picker-indicator": {
                                                display: "none",
                                                WebkitAppearance: "none",
                                            },
                                            "& input[type='date']": {
                                                appearance: "none",
                                                WebkitAppearance: "none",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    border: "none",
                                                },
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <Box sx={{
                                                    ml: 2,
                                                    mr: 1,
                                                    color: "#433F5E",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    cursor: "pointer"
                                                }}>
                                                    {calenderSVG}
                                                </Box>
                                            ),
                                            sx: {
                                                backgroundColor: "#fff",
                                                border: '1px solid',
                                                borderColor: 'hsl(0, 0%, 80%)',
                                                height: "40px",
                                                width: { xs: "100%", md: "100%" },
                                                borderRadius: "5px",
                                            },
                                        }}
                                    />
                                    {touched.gfcApprovalDate && errors.gfcApprovalDate && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.gfcApprovalDate}
                                        </Typography>
                                    )}
                                </Grid> */}

                                                {/* <Grid size={12}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px"
                                    }}>
                                        <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "#140F36", fontFamily: "Outfit, sans-serif", mb: 1, mt: 1 }}>
                                            Revision Section
                                        </Typography>
                                        <Button
                                            onClick={() => handleAddMoreClick(values, setFieldValue)}
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "3px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "10px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                color: "#FFFFFF",
                                                px: "10px",
                                                py: "6px",
                                                '&:disabled': {
                                                    backgroundColor: "#ccc",
                                                    color: "#666"
                                                }
                                            }}
                                        >
                                            Add Revision
                                        </Button>
                                    </Box>
                                </Grid> */}


                                                {/* {renderRevisionFields(values, handleChange, handleBlur, touched, errors, setFieldValue, setFieldTouched)} */}

                                                <Grid size={{ xs: 12, md: 12 }}>
                                                    <Typography sx={labelTextStyle}>
                                                        Remarks
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="remarks"
                                                        value={values.remarks}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        multiline
                                                        rows={3}
                                                        placeholder="Enter Remarks"
                                                        InputProps={{
                                                            sx: {
                                                                borderRadius: "8px",
                                                                backgroundColor: "#fff",
                                                                "& textarea::placeholder": {
                                                                    fontWeight: 400,
                                                                    fontSize: "16px",
                                                                    lineHeight: "100%",
                                                                    letterSpacing: "0.16px",
                                                                    color: "#0A0A0C",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                    opacity: 1,
                                                                },
                                                                "& textarea": {
                                                                    fontWeight: 400,
                                                                    fontSize: "16px",
                                                                    lineHeight: "100%",
                                                                    letterSpacing: "0.16px",
                                                                    color: "#0A0A0C",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                },
                                                            },
                                                        }}
                                                    />
                                                    {/* {touched.remarks && errors.remarks && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.remarks}
                                        </Typography>
                                    )} */}
                                                </Grid>
                                                <Grid size={12}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            alignItems: "center",
                                                            mt: 2,
                                                        }}
                                                    >
                                                        <Button
                                                            onClick={() => {
                                                                handleClose();
                                                                setRevisionCount(1);
                                                            }}
                                                            sx={{
                                                                textTransform: "none",
                                                                color: "#F15744",
                                                                fontSize: "14px",
                                                                lineHeight: "100%",
                                                                fontFamily: "Outfit, sans-serif",
                                                                borderRadius: "25px",
                                                                fontWeight: 500,
                                                                px: "20px",
                                                                py: "15px",
                                                                mr: 2,
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            type="submit"
                                                            variant="contained"
                                                            disabled={isSubmitting}
                                                            sx={{
                                                                backgroundColor: "#00344C",
                                                                borderRadius: "25px",
                                                                textTransform: "none",
                                                                fontWeight: 500,
                                                                fontSize: "14px",
                                                                lineHeight: "100%",
                                                                fontFamily: "Outfit, sans-serif",
                                                                color: "#FFFFFF",
                                                                px: "20px",
                                                                py: "15px",
                                                                "&:hover": {
                                                                    backgroundColor: "#002233",
                                                                },
                                                            }}
                                                        >
                                                            {isSubmitting ? "Saving..." : "Save Drawing"}
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </form>
                            </>
                        )
                    }}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default AddDesignEngineer;